import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { TEMPLATES } from "@/lib/email-templates/registry";
import { sendTemplateEmail } from "@/lib/email-templates/send-email";
import { LeadFieldsSchema, detectSpam } from "@/lib/leadValidation";
import { deviceFromUserAgent } from "@/lib/device";

const LeadSchema = LeadFieldsSchema.extend({
  source: z.string().trim().max(64).optional(),
  userAgent: z.string().trim().max(500).optional(),
  sessionId: z.string().trim().max(64).optional(),
  path: z.string().trim().max(512).optional(),
  placement: z.string().trim().max(64).optional(),
  variant: z.string().trim().max(64).optional(),
  // Spam protection: honeypot must stay empty, and the form must have been
  // on screen for a moment before submitting.
  hp: z.string().max(200).optional(),
  elapsedMs: z.number().int().nonnegative().max(86_400_000).optional(),
});

type LeadData = z.infer<typeof LeadSchema>;

async function logSend(row: {
  recipient_email: string;
  status: "sent" | "suppressed" | "failed";
  error_message?: string;
}) {
  const { error } = await supabaseAdmin.from("email_send_log").insert({
    message_id: null,
    template_name: "lead-notification",
    ...row,
  });
  if (error) console.error("[lead-notification] send log insert failed:", error.message);
}

async function enqueueLeadNotification(lead: LeadData, leadId?: string) {
  const template = TEMPLATES["lead-notification"];
  if (!template?.to) return;

  const templateData = {
    name: lead.name,
    email: lead.email,
    phone: lead.phone || undefined,
    company: lead.company || undefined,
    message: lead.message,
    source: lead.source || "contact_quick_form",
    submittedAt: new Date().toISOString(),
  };

  const idempotencyKey = leadId
    ? `lead-notify-${leadId}`
    : `lead-notify-${crypto.randomUUID()}`;

  try {
    const result = await sendTemplateEmail("lead-notification", template.to, {
      templateData,
      idempotencyKey,
    });
    await logSend({
      recipient_email: template.to,
      status: result.sent ? "sent" : "suppressed",
    });
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    console.error("[lead-notification] send failed:", message);
    await logSend({
      recipient_email: template.to,
      status: "failed",
      error_message: message.slice(0, 1000),
    });
  }
}

export const submitLead = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => LeadSchema.parse(input))
  .handler(async ({ data }) => {
    // --- Spam protection -------------------------------------------------
    // 1. Honeypot: only bots fill the hidden field.
    // 2. Timing: humans take longer than 2.5s to fill the form.
    // 3. Content heuristics (links / known spam terms).
    // Bots get a success-shaped response so they don't tune their payload.
    if (data.hp && data.hp.trim().length > 0) return { ok: true };
    if (typeof data.elapsedMs === "number" && data.elapsedMs < 2500) {
      return { ok: true };
    }
    const spam = detectSpam({
      name: data.name,
      message: data.message,
      company: data.company || undefined,
    });
    if (spam) throw new Error(spam);

    const [{ getRequestHeader }, { submitThrottled }] = await Promise.all([
      import("@tanstack/react-start/server"),
      import("@/lib/leads-guard.server"),
    ]);
    const ip =
      getRequestHeader("cf-connecting-ip") ??
      getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ??
      "unknown";
    if (submitThrottled(ip)) {
      throw new Error("Too many submissions. Please try again in a few minutes.");
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("contact_leads")
      .insert({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        message: data.message,
        source: data.source || "contact_quick_form",
        user_agent: data.userAgent || null,
        session_id: data.sessionId || null,
        path: data.path || null,
        placement: data.placement || null,
        variant: data.variant || null,
        device: deviceFromUserAgent(
          data.userAgent || getRequestHeader("user-agent") || null,
        ),
      })
      .select("id")
      .single();

    if (error) {
      console.error("[submitLead] insert failed:", error.message);
      throw new Error("Could not save your message. Please try again.");
    }

    // Fire-and-await notification enqueue so failures surface in logs,
    // but don't block the user response on email provider hiccups —
    // the queue dispatcher will handle retries.
    try {
      await enqueueLeadNotification(data, inserted?.id);
    } catch (e) {
      console.error("[submitLead] notification enqueue threw:", e);
    }

    return { ok: true };
  });

// Internal test trigger — enqueues a sample lead notification without
// writing to contact_leads. Guarded by ADMIN_TOKEN.
export const sendTestLeadNotification = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(8) }).parse(input),
  )
  .handler(async ({ data }) => {
    const expected = process.env.ADMIN_TOKEN ?? "";
    const provided = data.token;
    if (!expected || provided.length !== expected.length) {
      throw new Error("Unauthorized");
    }
    const { timingSafeEqual } = await import("node:crypto");
    if (!timingSafeEqual(Buffer.from(provided), Buffer.from(expected))) {
      throw new Error("Unauthorized");
    }
    await enqueueLeadNotification({
      name: "Test Lead",
      email: "test@example.com",
      phone: "+91 9999999999",
      company: "Deliverability Check",
      message:
        "This is a test notification to confirm notify.arifkhanglobal.com is delivering email.",
      source: "test_trigger",
    });
    return { ok: true };
  });
